import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SearchIcon, ShieldCheckIcon, ClockIcon, MinusCircleIcon, CheckCircle2Icon, UserPlusIcon, HashIcon, ChevronDownIcon } from 'lucide-react';
import { User, LicenseData, MichiganLicenseData, StateStatus, createDefaultLicense, createDefaultMichiganLicense } from './types';
interface StateLicenseModalProps {
  stateName: string;
  isOpen: boolean;
  onClose: () => void;
  users: User[];
  licenses: Record<string, Record<string, LicenseData>>;
  michiganLicenses: Record<string, MichiganLicenseData>;
  onUpdateLicense: (userId: string, stateName: string, license: LicenseData) => void;
  onUpdateMichiganLicense: (userId: string, license: MichiganLicenseData) => void;
  onAddUserToState: (userId: string, stateName: string) => void;
  onOpenUserStateDetail: (userId: string, stateName: string) => void;
}
const STATUS_CONFIG: Record<StateStatus, {
  label: string;
  icon: typeof CheckCircle2Icon;
  color: string;
}> = {
  not_started: {
    label: 'Not Started',
    icon: MinusCircleIcon,
    color: 'text-slate-500'
  },
  in_progress: {
    label: 'In Progress',
    icon: ClockIcon,
    color: 'text-amber-400'
  },
  complete: {
    label: 'Licensed',
    icon: CheckCircle2Icon,
    color: 'text-emerald-400'
  }
};
export function StateLicenseModal({
  stateName,
  isOpen,
  onClose,
  users,
  licenses,
  michiganLicenses,
  onUpdateLicense,
  onUpdateMichiganLicense,
  onAddUserToState,
  onOpenUserStateDetail
}: StateLicenseModalProps) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'assigned' | 'unassigned'>('assigned');
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const isMichigan = stateName === 'Michigan';
  const getUserStatus = (userId: string): StateStatus => {
    if (isMichigan) {
      const mi = michiganLicenses[userId];
      if (!mi) return 'not_started';
      if (mi.steps.every(Boolean)) return 'complete';
      if (mi.steps.some(Boolean)) return 'in_progress';
      return 'not_started';
    }
    return licenses[userId]?.[stateName]?.status || 'not_started';
  };
  const isUserAssigned = (userId: string): boolean => {
    if (isMichigan) return !!michiganLicenses[userId];
    return !!licenses[userId]?.[stateName];
  };
  const assignedUsers = useMemo(() => {
    const q = search.toLowerCase().trim();
    return users.filter(u => isUserAssigned(u.id)).filter(u => !q || u.name.toLowerCase().includes(q) || u.nmlsId.toLowerCase().includes(q)).sort((a, b) => {
      const aStatus = getUserStatus(a.id);
      const bStatus = getUserStatus(b.id);
      const order: Record<StateStatus, number> = {
        complete: 0,
        in_progress: 1,
        not_started: 2
      };
      return order[aStatus] - order[bStatus] || a.name.localeCompare(b.name);
    });
  }, [users, licenses, michiganLicenses, search, stateName]);
  const unassignedUsers = useMemo(() => {
    const q = addSearch.toLowerCase().trim();
    return users.filter(u => !isUserAssigned(u.id)).filter(u => !q || u.name.toLowerCase().includes(q) || u.nmlsId.toLowerCase().includes(q)).sort((a, b) => a.name.localeCompare(b.name));
  }, [users, licenses, michiganLicenses, addSearch, stateName]);
  const counts = useMemo(() => {
    let licensed = 0;
    let inProgress = 0;
    let total = 0;
    for (const user of users) {
      if (!isUserAssigned(user.id)) continue;
      total++;
      const status = getUserStatus(user.id);
      if (status === 'complete') licensed++;else if (status === 'in_progress') inProgress++;
    }
    return {
      licensed,
      inProgress,
      total
    };
  }, [users, licenses, michiganLicenses, stateName]);
  const handleQuickStatusChange = (userId: string, newStatus: StateStatus) => {
    if (isMichigan) {
      const existing = michiganLicenses[userId] || createDefaultMichiganLicense();
      if (newStatus === 'complete') {
        onUpdateMichiganLicense(userId, {
          ...existing,
          steps: [true, true, true, true, true, true]
        });
      } else if (newStatus === 'not_started') {
        onUpdateMichiganLicense(userId, {
          ...existing,
          steps: [false, false, false, false, false, false]
        });
      }
    } else {
      const existing = licenses[userId]?.[stateName] || createDefaultLicense();
      onUpdateLicense(userId, stateName, {
        ...existing,
        status: newStatus
      });
    }
  };
  const handleAddUser = (userId: string) => {
    onAddUserToState(userId, stateName);
    setAddSearch('');
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-189">
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-190">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-191" />
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className={`relative w-full max-w-2xl bg-slate-900 border rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] ${isMichigan ? 'border-amber-500/30 shadow-[0_0_40px_rgba(245,158,11,0.1)]' : 'border-slate-700/50'}`} data-id="element-192">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800" data-id="element-193">
            <div data-id="element-194">
              <h2 className="text-xl font-bold text-white" data-id="element-195">{stateName}</h2>
              <div className="flex items-center gap-3 mt-1" data-id="element-196">
                {counts.licensed > 0 && <span className="flex items-center gap-1 text-xs text-emerald-400" data-id="element-197">
                    <ShieldCheckIcon className="w-3 h-3" data-id="element-198" />
                    {counts.licensed} licensed
                  </span>}
                {counts.inProgress > 0 && <span className="flex items-center gap-1 text-xs text-amber-400" data-id="element-199">
                    <ClockIcon className="w-3 h-3" data-id="element-200" />
                    {counts.inProgress} in progress
                  </span>}
                <span className="text-xs text-slate-500" data-id="element-201">
                  {counts.total} assigned
                </span>
              </div>
            </div>
            <button onClick={onClose} className="p-2 -mr-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-colors" data-id="element-202">
              <XIcon className="w-5 h-5" data-id="element-203" />
            </button>
          </div>

          {/* Add User Dropdown */}
          <div className="px-6 py-3 border-b border-slate-800/50" data-id="element-204">
            <div className="relative" data-id="element-205">
              <button onClick={() => setShowAddDropdown(!showAddDropdown)} className="flex items-center gap-2 px-3.5 py-2 bg-emerald-600/10 hover:bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 text-sm font-medium rounded-xl transition-colors w-full justify-center" data-id="element-206">
                <UserPlusIcon className="w-4 h-4" data-id="element-207" />
                Add Users to {stateName}
                <ChevronDownIcon className={`w-4 h-4 transition-transform ${showAddDropdown ? 'rotate-180' : ''}`} data-id="element-208" />
              </button>

              <AnimatePresence data-id="element-209">
                {showAddDropdown && <motion.div initial={{
                opacity: 0,
                y: -4,
                height: 0
              }} animate={{
                opacity: 1,
                y: 0,
                height: 'auto'
              }} exit={{
                opacity: 0,
                y: -4,
                height: 0
              }} className="mt-2 bg-slate-800/80 border border-slate-700/50 rounded-xl overflow-hidden" data-id="element-210">
                    <div className="p-2" data-id="element-211">
                      <div className="flex items-center bg-slate-900/50 rounded-lg px-3 py-2 mb-2" data-id="element-212">
                        <SearchIcon className="w-3.5 h-3.5 text-slate-500 mr-2" data-id="element-213" />
                        <input type="text" value={addSearch} onChange={e => setAddSearch(e.target.value)} placeholder="Search unassigned users…" className="w-full bg-transparent text-sm text-slate-200 focus:outline-none placeholder:text-slate-500" autoFocus data-id="element-214" />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto custom-scrollbar px-2 pb-2 space-y-0.5" data-id="element-215">
                      {unassignedUsers.length === 0 ? <div className="text-center py-4 text-xs text-slate-500" data-id="element-216">
                          {users.length === 0 ? 'No users created yet' : 'All users are already assigned'}
                        </div> : unassignedUsers.map(user => <button key={user.id} onClick={() => handleAddUser(user.id)} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-700/50 transition-colors text-left" data-id="element-217">
                            <div className="w-7 h-7 rounded-lg bg-slate-700/50 flex items-center justify-center text-[10px] font-bold text-slate-400" data-id="element-218">
                              {user.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-219">
                              <span className="text-sm text-slate-200 truncate block" data-id="element-220">
                                {user.name}
                              </span>
                              <span className="text-[10px] text-slate-500 tabular-nums" data-id="element-221">
                                #{user.nmlsId}
                              </span>
                            </div>
                            <UserPlusIcon className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" data-id="element-222" />
                          </button>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          {/* Search Assigned Users */}
          <div className="px-6 py-2.5 border-b border-slate-800/30" data-id="element-223">
            <div className="flex items-center bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-2 focus-within:border-emerald-500/50 transition-all" data-id="element-224">
              <SearchIcon className="w-3.5 h-3.5 text-slate-500 mr-2" data-id="element-225" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search assigned users…" className="w-full bg-transparent text-sm text-slate-200 focus:outline-none placeholder:text-slate-500" data-id="element-226" />
            </div>
          </div>

          {/* User List */}
          <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-1.5" data-id="element-227">
            {assignedUsers.length === 0 ? <div className="text-center py-12 space-y-2" data-id="element-228">
                <p className="text-sm text-slate-400" data-id="element-229">
                  No users assigned to {stateName}
                </p>
                <p className="text-xs text-slate-500" data-id="element-230">
                  Use the button above to add users
                </p>
              </div> : assignedUsers.map(user => {
            const status = getUserStatus(user.id);
            const config = STATUS_CONFIG[status];
            const StatusIcon = config.icon;
            return <div key={user.id} className="flex items-center gap-3 px-4 py-3 bg-slate-800/30 hover:bg-slate-800/50 border border-slate-700/40 rounded-xl transition-all group" data-id="element-231">
                    {/* Avatar */}
                    <div className="w-9 h-9 rounded-lg bg-slate-700/50 border border-slate-600/50 flex items-center justify-center text-xs font-bold text-slate-300 flex-shrink-0" data-id="element-232">
                      {user.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0" data-id="element-233">
                      <span className="text-sm font-medium text-white truncate block" data-id="element-234">
                        {user.name}
                      </span>
                      <div className="flex items-center gap-1" data-id="element-235">
                        <HashIcon className="w-2.5 h-2.5 text-slate-500" data-id="element-236" />
                        <span className="text-[10px] text-slate-500 tabular-nums" data-id="element-237">
                          {user.nmlsId}
                        </span>
                      </div>
                    </div>

                    {/* Quick Status Toggle */}
                    {!isMichigan && <div className="flex bg-slate-800/80 rounded-lg p-0.5 gap-0.5 flex-shrink-0" data-id="element-238">
                        <button onClick={() => handleQuickStatusChange(user.id, 'not_started')} className={`p-1.5 rounded-md transition-colors ${status === 'not_started' ? 'bg-slate-700 text-slate-200' : 'text-slate-500 hover:text-slate-300'}`} aria-label="Set not started" data-id="element-239">
                          <MinusCircleIcon className="w-3.5 h-3.5" data-id="element-240" />
                        </button>
                        <button onClick={() => handleQuickStatusChange(user.id, 'in_progress')} className={`p-1.5 rounded-md transition-colors ${status === 'in_progress' ? 'bg-amber-500/20 text-amber-400' : 'text-slate-500 hover:text-slate-300'}`} aria-label="Set in progress" data-id="element-241">
                          <ClockIcon className="w-3.5 h-3.5" data-id="element-242" />
                        </button>
                        <button onClick={() => handleQuickStatusChange(user.id, 'complete')} className={`p-1.5 rounded-md transition-colors ${status === 'complete' ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-500 hover:text-slate-300'}`} aria-label="Set complete" data-id="element-243">
                          <CheckCircle2Icon className="w-3.5 h-3.5" data-id="element-244" />
                        </button>
                      </div>}

                    {/* Michigan status badge */}
                    {isMichigan && <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg ${status === 'complete' ? 'bg-emerald-500/10' : status === 'in_progress' ? 'bg-amber-500/10' : 'bg-slate-800/50'}`} data-id="element-245">
                        <StatusIcon className={`w-3.5 h-3.5 ${config.color}`} data-id="element-246" />
                        <span className={`text-xs font-medium ${config.color}`} data-id="element-247">
                          {config.label}
                        </span>
                      </div>}

                    {/* Detail button */}
                    <button onClick={() => onOpenUserStateDetail(user.id, stateName)} className="p-1.5 text-slate-500 hover:text-white hover:bg-slate-700 rounded-lg transition-colors opacity-0 group-hover:opacity-100" aria-label={`View details for ${user.name}`} data-id="element-248">
                      <ChevronDownIcon className="w-4 h-4 -rotate-90" data-id="element-249" />
                    </button>
                  </div>;
          })}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-800 flex justify-end" data-id="element-250">
            <button onClick={onClose} className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-xl transition-colors" data-id="element-251">
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}